
import { defineComponent } from 'vue';
import LocationState, { ILocationState } from '../../store/modules/locations';
import { getModule } from 'vuex-module-decorators';
import { ILocation, ILocationIndexViewModel, ILocationListDisplay } from '../../store/interfaces/ILocation';
import MapContainer from '../../components/MapContainer.vue';
import ModelSelector from '../../components/ModelSelector.vue';
import RoomState, { IRoomState, IUnassignedRoomResponse } from '../../store/modules/rooms';
import { IRoom, IRoomListDisplay } from '../../store/interfaces/IRoom';
import { IModelSelectionPool } from '../../store/interfaces/IModelSelectionPool';
import 'bootstrap/dist/css/bootstrap.min.css';
import { mdiCheckBold } from '@mdi/js';
import PageDirtyService from '../../services/PageDirtyService';
import TierState, { ITierState } from '../../store/modules/tier';

let locationState = {} as ILocationState;
let roomState = {} as IRoomState;
let defaultLocation = {} as ILocation;
let tierState = {} as ITierState;

export default defineComponent({
	name: 'LocationDetailsView',
	data() {
		return {
			location: {} as ILocation,
			childLocations: [] as ILocationListDisplay[],
			allRooms: [] as IRoomListDisplay[],  // Rooms in this location + sublocations (for table display)
			unassignedRooms: [] as IRoomListDisplay[],  // Unassigned rooms (lazy loaded for modal)
			isEmergencyAlertsEnabled: false,
			subLocationTableSelection: [] as ILocation[],
			roomTableSelection: [] as IRoom[],
			showRoomSelector: false,
			locationSearchInput: '',
			loading: true,
			saving: false,
			roomSearchInput: '',
			roomPaging: {
				pageIndex: 1,
				pageSize: 10000,
				sortField: 'roomName',
				sortOrder: 'asc'
			} as any,
			roomTableLoading: false,
			sublocationTableLoading: false,
			manageRoomsLoading: false,
			rules: {
				name: [
					{
						required: true,
						message: 'Please enter a Location name.',
						trigger: 'blur',
					},
				],
			},
			mdiCheckBold,
		};
	},
	components: {
		MapContainer,
		ModelSelector,
	},
	props: ['creationMode', 'id'],
	computed: {
		processedChildLocations() {
			if(!this.childLocations || this.childLocations.length == 0) return [] as ILocationIndexViewModel[];

			const processedChildLocs = [] as ILocationIndexViewModel[];
			for(const cLoc of this.childLocations) {
				const roomCount = cLoc.rooms ? cLoc.rooms.length : 0;
				let receiverCount = 0;

				for(const room of cLoc.rooms) {
					receiverCount += room._devices ? room._devices.length : 0;
				}

				processedChildLocs.push({
					id: cLoc.id,
					name: cLoc.name,
					deviceCount: receiverCount,
					roomCount: roomCount
				});
			}

			return processedChildLocs;
		},
		locationOrSublocation(): (capital: boolean) => string {
			return (capital: boolean) => {
				let returnVal = this.location.parentLocation && this.location.parentLocation.id ? 'sublocation' : 'location';
				if (capital) {
					returnVal = this.location.parentLocation && this.location.parentLocation.id ? 'Sublocation' : 'Location';
				}

				return returnVal;
			};
		},
	locationRooms(): IRoomListDisplay[] | undefined {
		if (!this.allRooms || this.allRooms.length === 0) return undefined;

		// Filter to show only rooms in this location + sublocations (exclude unassigned)
		const locationAndSubLocationRooms = this.allRooms.filter((x: IRoomListDisplay) => {
			if (!x.location || !x.location.id) return false;  // Exclude unassigned
			// Include rooms in this location or its sublocations
			return x.location.id === this.location.id || 
				x.location.parentId === this.location.id;
		});

		if (!this.roomSearchInput || this.roomSearchInput === '') return locationAndSubLocationRooms;

		return locationAndSubLocationRooms.filter(
			(x:IRoomListDisplay) =>
				x.roomName.toLowerCase().includes(this.roomSearchInput.toLowerCase()) ||
				(x.roomCode && x.roomCode.toLowerCase().includes(this.roomSearchInput.toLowerCase()))
		);
	}
	},
	methods: {
		handleSublocationClick(row: any, column: any) {
			if (column.property === 'id') return;
			this.$router.push({ path: `/locations/${row.id}` });
		},
		handleSublocationSortChange({ order }: any) {
			// If sort is cleared (no order), revert to default sort by name ascending
			if (!order || order === null) {
				this.$nextTick(() => {
					const table = (this.$refs.sublocationTable as any);
					if (table) {
						table.sort('name', 'ascending');
					}
				});
			}
		},
		handleRoomClick(row: any, column: any) {
			if (column.property === 'id') return;
			this.$router.push({ path: `/rooms/${row.id}` });
		},
		addReceiverLink() {
			this.$router.push({ path: '/receivers' });
		},
		validateForm(): Promise<boolean> {
			return new Promise((resolve) => {
				(this as any).$refs['form'].validate((valid: boolean, obj: any) => {
					if (!valid) {
						let errorHTML = '';
						for (const key in obj) {
							for (const err of obj[key]) {
								errorHTML = `${errorHTML}${err.message}<br>`;
							}
						}
						console.error(errorHTML);
						(this as any).$message({
							dangerouslyUseHTMLString: true,
							message: errorHTML,
							type: 'error',
						});
					}

					resolve(valid);
				});
			});
		},
		async onCancel() {
			if (this.location.parentLocation) {
				(this as any).$refs['form'].clearValidate();
				this.$router.push({ path: `/locations/${this.location.parentLocation.id}` });
				return;
			}

			this.$router.push({ path: '/locations' });
		},
		async onSave() {
			if (!(await this.validateForm())) return;
			this.saving = true;
			const storeResponse = await locationState.updateLocation(this.location);

			if (!storeResponse.success) {
				(this as any).$message.error(
					storeResponse.reason ? `Location update failed: ${storeResponse.reason}` : 'Location update failed'
				);
				this.saving = false;
				return;
			}

			(this as any).$message.success('Location saved');
			this.saving = false;
			await this.loadLocation();
		},
		async onCreate() {
			if (!(await this.validateForm())) return;
			this.saving = true;
			// this is a trigger for the API to set this location as a child.
			// that makes it OK to do bad stuff. We don't want the interface to reflect that property
			(this.location as any).parentId = this.$route.params.id;

			const storeResponse = await locationState.createLocation(this.location);

			if (!storeResponse.success) {
				(this as any).$message.error(
					storeResponse.reason ? `Location creation failed: ${storeResponse.reason}` : 'Location creation failed'
				);
				this.saving = false;
				return;
			}

			(this as any).$message.success('Location created');
			this.saving = false;
			if (!storeResponse.data) return;
			PageDirtyService.markPageClean();
			this.$router.push({
				path: `/locations/${ storeResponse.data.parentLocation ? storeResponse.data.parentLocation.id : storeResponse.data.id }`
			});
		},
		async onDelete() {
			if (!this.location || !this.location.id) return; // can't delete something we don't have!

			try {
				await (this as any).$confirm(
					'Are you sure you want to delete this location?',
					'Warning',
					{
						confirmButtonText: 'Delete',
						cancelButtonText: 'Cancel',
						type: 'warning',
					}
				);
				this.saving = true;

				await locationState.deleteLocation(this.location.id);

        PageDirtyService.markPageClean();

        if (this.location.parentLocation) {
          this.$router.push({
            path: `/locations/${this.location.parentLocation.id}`,
          });
          this.saving = false;
          return;
        }
        this.saving = false;
        this.$router.push({ path: "/locations" });
        return;
      } catch {
        //canceled
      }
    },
		async onRemoveSublocations() {
			if (!this.subLocationTableSelection || this.subLocationTableSelection.length === 0)
				return;
			try {
				let confirmMessage = '';
                if(this.subLocationTableSelection.length === this.childLocations.length && this.subLocationTableSelection.length > 1) {
                    confirmMessage = `Are you sure you want to delete all ${this.subLocationTableSelection.length} sublocations?`;
                }
                else if( this.subLocationTableSelection.length === 1) {
                    confirmMessage = `Are you sure you want to delete this sublocation?`;
                }
                else {
                    confirmMessage = `Are you sure you want to delete ${this.subLocationTableSelection.length} of ${this.childLocations.length} sublocations'?`;
                }
				await (this as any).$confirm(
					confirmMessage,
					'Warning',
					{
						confirmButtonText: 'Delete',
						cancelButtonText: 'Cancel',
						type: 'warning'
					}
				);
				this.saving = true;
				const locationDeletePromise = [];

				for (const location of this.subLocationTableSelection) {
					locationDeletePromise.push(locationState.deleteLocation(location.id));
				}

				const responses = await Promise.all(locationDeletePromise);

				const failureResponses = responses.filter((x) => !x.success);

				if (failureResponses && failureResponses.length > 0) {
					for (const response of failureResponses) {
						(this as any).$message.error(
							response.reason ? `Removing sublocations failed: ${response.reason}` : 'Removing sublocations failed'
						);
					}
					
					await this.loadLocation();
					this.saving = false;
					return;
				}

				await this.loadLocation();
				(this as any).$message.success('Sublocations removed');
				this.saving = false;
			} catch {
				//canceled
			}
		},
		onEnter() {
			if (this.creationMode) {
				this.onCreate();
			} else {
				this.onSave();
			}
		},
		changeAlertPolygon(coordinates: []) {
			this.location.alertPolygon = coordinates;
		},
		async loadLocation() {
			this.location = {} as ILocation;

			if (this.creationMode) {
				this.location.parentLocation = this.$route.params.id ? (await locationState.fetchLocation(this.$route.params.id as string)).data : {} as ILocation;
				defaultLocation = Object.assign({}, this.location);
				this.loading = false;
				return;
			}

			// Reset loading states and clear data to show loaders and prevent stale data
			this.loading = true;
			this.sublocationTableLoading = false;
			this.roomTableLoading = false;
			this.childLocations = [];
			this.allRooms = [];
			this.unassignedRooms = [];

			// Wait for Vue to render the loader before starting API calls
			await (this as any).$nextTick();

			// Fetch location details (full screen loader shows)
			const locationResponse = await locationState.fetchLocation(this.$route.params.id as string);
			this.location = locationResponse.data ?? {} as ILocation;
			defaultLocation = Object.assign({}, this.location);
			
			// Hide full screen loader (location details loaded, show form)
			this.loading = false;
			
			// Fetch sublocations and rooms in parallel (separate loaders)
			await Promise.all([
				this.loadSublocations(),
				this.loadLocationRooms()
			]);
		},
		async loadSublocations() {
			if (!this.location || !this.location.id) {
				this.childLocations = [];
				return;
			}
			
			this.sublocationTableLoading = true;
			
			try {
				const subLocationResponse = await locationState.fetchLocationDisplayList({
					pageIndex: 1, 
					pageSize: 10000, 
					parentId: this.location.id
				});
				this.childLocations = (subLocationResponse.data && subLocationResponse.data.data) 
					? subLocationResponse.data.data 
					: [] as ILocationListDisplay[];
			} catch (error) {
				console.error('Error loading sublocations:', error);
				this.childLocations = [];
			} finally {
				this.sublocationTableLoading = false;
			}
		},
		async loadLocationRooms() {
			if (!this.location || !this.location.id) {
				this.allRooms = [];
				return;
			}

			this.roomTableLoading = true;

			const roomSearchParams: any = {
				pageIndex: this.roomPaging.pageIndex,
				pageSize: this.roomPaging.pageSize,
				locationId: this.location.id,
				sortField: this.roomPaging.sortField,
				sortOrder: this.roomPaging.sortOrder
			};

			const roomsResponse = await roomState.fetchRoomDisplayList(roomSearchParams);
			if(roomsResponse.success && roomsResponse.data && roomsResponse.data.data && roomsResponse.data.data.length > 0) {
				this.allRooms = roomsResponse.data.data;
			} else {
				this.allRooms = [];
			}

			this.roomTableLoading = false;
		},
		normalizeUnassignedRoom(room: IUnassignedRoomResponse): IRoomListDisplay {
			return {
				id: room.id,
				roomName: room.roomName,
				roomCode: room.roomCode || '',
				location: {
					id: '',
					name: '',
					parentId: '',
					parentName: ''
				},
				devices: [],
				recstatuses: [],
				status: 0,
				signageListCount: 0
			};
		},
		async loadUnassignedRooms() {
			// Return cached data if already loaded
			if (this.unassignedRooms && this.unassignedRooms.length > 0) {
				return this.unassignedRooms;
			}

			// Use new dedicated unassigned rooms endpoint
			const searchParams = {
				pageIndex: 1,
				pageSize: 10000,
				sortField: 'roomName',
				sortOrder: 'asc' as 'asc' | 'desc'
			};

			const roomsResponse = await roomState.fetchUnassignedRooms(searchParams);
			if(roomsResponse.success && roomsResponse.data && roomsResponse.data.data) {
				// Normalize minimal unassigned room data to IRoomListDisplay format
				this.unassignedRooms = roomsResponse.data.data.map(room => 
					this.normalizeUnassignedRoom(room)
				);
			} else {
				this.unassignedRooms = [];
			}

			return this.unassignedRooms;
		},
		sortTable(e: any) {
			this.roomPaging.sortField = !e.prop ? 'roomName' : e.prop.split(',')[0];
			this.roomPaging.sortOrder = e.order == 'descending' ? 'desc' : 'asc';
			this.loadLocationRooms();
		},
		handleSublocationSelectionChange(val: ILocation[]) {
			this.subLocationTableSelection = val;
		},
		async activateRoomManagementSelector() {
			this.manageRoomsLoading = true;

			try {
				// Lazy load unassigned rooms when modal opens (if not already cached)
				await this.loadUnassignedRooms();

				// Get rooms in this location + sublocations (matches table view)
				const locationAndSubLocationRooms = this.allRooms.filter((x: IRoomListDisplay) => {
					if (!x.location || !x.location.id) return false;
					return x.location.id === this.location.id || x.location.parentId === this.location.id;
				});

				// Merge unassigned rooms + location rooms (exclude sublocations)
				// Deduplicate by room ID to prevent showing same room twice
				const roomMap = new Map<string, IRoomListDisplay>();
				
				// Add unassigned rooms first
				this.unassignedRooms.forEach((room) => {
					roomMap.set(room.id, room);
				});
				
				// Add location + sublocation rooms (overwrite if duplicate)
				locationAndSubLocationRooms.forEach((room) => {
					roomMap.set(room.id, room);
				});

				const availableRooms = Array.from(roomMap.values());

				const availableRoomsMap = availableRooms.map((x) => {
						return {
							id: x.id,
							name: x.roomName,
							selected: !!(x.location && (x.location.id == this.location.id || x.location.parentId === this.location.id))
						};
					}
				) as IModelSelectionPool[];

				(this.$refs as any).modelSelector.activate(availableRoomsMap);
			} finally {
				this.manageRoomsLoading = false;
			}
		},
		async roomSelectionChanged(selection: IModelSelectionPool[]) {
			this.saving = true;

			const allRoomsResponse = await roomState.fetchRoomDisplayList({pageIndex: 1, pageSize: 10000});
			const allRoomsList = (allRoomsResponse.success && allRoomsResponse.data && allRoomsResponse.data.data) 
				? allRoomsResponse.data.data 
				: [] as IRoomListDisplay[];

			// get list of rooms that are un selected and have a location... they get location removed...
			const unSelectedRoomIds = selection.filter((x) => !x.selected).map((x) => x.id);
			const removeLocationRoomIds = allRoomsList.filter((x: IRoomListDisplay) => {
				// skip if not in the un selected list
				if(!unSelectedRoomIds.includes(x.id)) return false;
				// if we it has a location we need to remove it
				if(x.location && x.location.id) return true;
				// skip everything else
				return false;
			}).map(x=>x.id);
			
			// get a list or rooms that are selected and their location was null
			const selectedRoomIds = selection.filter((x) => x.selected).map((x) => x.id);
			const addLocationRoomIds = allRoomsList.filter((x: IRoomListDisplay) => {
				// skip if not in the selected list
				if(!selectedRoomIds.includes(x.id)) return false;
				// if we don't have a location we need to add it
				if(!x.location || !x.location.id) return true;
				// skip everything else
				return false;
			}).map(x=>x.id);

			const promiseArray = [];

			if(addLocationRoomIds && addLocationRoomIds.length >0) {
				console.log('here add location', this.location.id);		
				promiseArray.push(roomState.addLocation({locationId: this.location.id, roomIds: addLocationRoomIds}))
			}

			if(removeLocationRoomIds && removeLocationRoomIds.length >0) {
				promiseArray.push(roomState.removeLocation({locationId: this.location.id, roomIds: removeLocationRoomIds}));
			}

			if(promiseArray.length == 0) {
				(this as any).$message.success('Room associations updated');
				this.saving = false;
				return;
			}
			
			const responses = await Promise.all(promiseArray);
			
			
			const failureResponses = responses.filter((x) => !x.success);

			if (failureResponses && failureResponses.length > 0) {
				for (const response of failureResponses) {
					(this as any).$message.error(
						response.reason ? `Managing rooms for location failed: ${response.reason}` : 'Managing rooms for location failed'
					);
					this.saving = false;
					return;
				}
			}

			await this.loadLocation();

			(this as any).$message.success('Room associations updated');
			this.saving = false;
		},
	},
	async created() {
		locationState = getModule(LocationState);
		roomState = getModule(RoomState);
		tierState = getModule(TierState);

		await this.loadLocation();
		await Promise.all([
			tierState.fetchTier()
		]);

		this.isEmergencyAlertsEnabled = tierState.orgTier.emergencyAlertsEnabled;

		this.loading = false;
	},
	watch: {
		async creationMode() {
			await this.loadLocation();
			(this.$refs as any).mapContainer.clear();
		},
		async id() {
			await this.loadLocation();
			(this.$refs as any).mapContainer.clear();
		},
		location: {
			handler (e) {
				PageDirtyService.monitorObjectSet([{defaultObject: defaultLocation, mutatedObject: e}]);
			},
			deep: true
		}
	},
});
